/**
 * Resumable pipeline state machine (Blueprint v2 P7). Each stage writes a
 * checkpoint artifact to `~/.futurex/pipelines/<id>/<stage>.json`; re-running
 * skips already-checkpointed stages, so killing the process mid-pipeline and
 * re-running resumes from the last checkpoint instead of restarting from zero.
 */
import { createHash } from 'node:crypto';
import { existsSync, mkdirSync, readdirSync, readFileSync, rmSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { futurexHome } from './config.js';

/** Stable pipeline id from a seed (e.g. `${repoRoot}::${task}`). */
export function pipelineId(seed: string): string {
  return createHash('sha1').update(seed).digest('hex').slice(0, 16);
}

function pipelineDir(id: string): string {
  return join(futurexHome(), 'pipelines', id);
}

export interface Checkpoint {
  readonly stage: string;
  readonly createdAt: string;
  readonly data: unknown;
}

/** Persist a stage's output as a checkpoint artifact. */
export function writeCheckpoint(id: string, stage: string, data: unknown): void {
  const dir = pipelineDir(id);
  mkdirSync(dir, { recursive: true });
  const cp: Checkpoint = { stage, createdAt: new Date().toISOString(), data };
  writeFileSync(join(dir, `${stage}.json`), JSON.stringify(cp), { mode: 0o600 });
}

/** Load a stage's checkpoint, or null when absent/unreadable. */
export function readCheckpoint(id: string, stage: string): Checkpoint | null {
  const path = join(pipelineDir(id), `${stage}.json`);
  if (!existsSync(path)) return null;
  try {
    return JSON.parse(readFileSync(path, 'utf8')) as Checkpoint;
  } catch {
    return null;
  }
}

/** Stage names checkpointed for a pipeline (no order guarantee). */
export function listCheckpoints(id: string): string[] {
  try {
    return readdirSync(pipelineDir(id))
      .filter((f) => f.endsWith('.json'))
      .map((f) => f.slice(0, -'.json'.length));
  } catch {
    return [];
  }
}

/** Remove a pipeline's checkpoints whose stage name starts with `prefix`. */
export function clearCheckpoints(id: string, prefix: string): void {
  for (const stage of listCheckpoints(id)) {
    if (!stage.startsWith(prefix)) continue;
    try {
      rmSync(join(pipelineDir(id), `${stage}.json`), { force: true });
    } catch {
      /* best effort */
    }
  }
}

/** Pipeline ids that have at least one checkpoint on disk. */
export function listPipelines(): string[] {
  try {
    return readdirSync(join(futurexHome(), 'pipelines'));
  } catch {
    return [];
  }
}

export interface PipelineStage {
  readonly name: string;
  /** Receives the results of prior stages, keyed by stage name. */
  run(prior: Record<string, unknown>): Promise<unknown>;
}

export type StageStatus = 'cached' | 'run';

/**
 * Run `stages` in order, checkpointing each. A stage already checkpointed is
 * skipped and its cached result reused (resume), unless `restart` is set.
 */
export async function runResumable(
  id: string,
  stages: readonly PipelineStage[],
  opts: { restart?: boolean; onStage?: (name: string, status: StageStatus) => void } = {},
): Promise<Record<string, unknown>> {
  const results: Record<string, unknown> = {};
  for (const stage of stages) {
    if (!opts.restart) {
      const cp = readCheckpoint(id, stage.name);
      if (cp) {
        results[stage.name] = cp.data;
        opts.onStage?.(stage.name, 'cached');
        continue;
      }
    }
    opts.onStage?.(stage.name, 'run');
    const data = await stage.run(results);
    writeCheckpoint(id, stage.name, data);
    results[stage.name] = data;
  }
  return results;
}
